import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  ChangeEvent,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Alert } from 'src/latest/src';
import {
  getStripePaymentMethods,
  putStripePaymentMethods,
  stripePaymentComplete,
} from 'src/redux/services/commonServices';
import { Logger } from 'src/latest/src/services';
import './StripeCard.scss';
import StripePoweredBy from '../PoweredBy/StripePoweredBy';
import { useDispatch, useSelector } from 'react-redux';
import { setFlashNotification } from 'src/redux/modules/flashNotification';
import { useTranslation } from 'react-i18next';
import MiniCheckBox from 'src/components/modules/MiniCheckBox/MiniCheckBox';
import Input from '../../../Input/Input';
import DropdownContainer from '../../../DropDownMenu/DropDownContainer';
import { address } from '../../StripePaymentInterface';
import {
  addressErrorInitialState,
  addressInitialState,
  StripeOnTokenMethod,
  usStatesChoices,
} from '../../StripePaymentConstant';
import { noop } from 'lodash';
import { User } from 'src/latest/src/components/UserProfileHeader/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import cardOnFileIcon from '../../../../../../../assets/icons/cards-onfile-icon.svg';

interface StripeCardProps {
  clientSecret: string;
  clientPaymentProfileId: string;
  onToken?: (status: string, choice: any) => void;
  choice: any;
  buttonLabel: string;
  onCardUpdated: (consent: string) => void;
  fromAddScreen: boolean;
  accountAddress: address;
  responsiblePartyAccountAddress: any;
  fromOnboarding: boolean;
  userData: User | null;
  handleLeftArrowClick: () => void;
  hideBackButton: boolean;
  isDirectoryPlatform: boolean;
  onFormValidationChange?: (isValid: boolean) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
}

interface AddressState {
  address: string;
  city: string;
  state: string;
  zip: string;
  cardHolderName: string;
  cardNickName: string;
}

interface AddressErrors {
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  cardHolderName: string | null;
  cardNickName: string | null;
}

const StripeCard = forwardRef<any, StripeCardProps>(
  (
    {
      clientSecret,
      clientPaymentProfileId,
      onToken,
      choice,
      buttonLabel,
      onCardUpdated,
      fromAddScreen,
      accountAddress,
      responsiblePartyAccountAddress,
      fromOnboarding,
      userData,
      handleLeftArrowClick = noop,
      hideBackButton,
      isDirectoryPlatform,
      onFormValidationChange,
      onProcessingChange,
    },
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [numberError, setNumberError] = useState<string | null>(null);
    const [expiryError, setExpiryError] = useState<string | null>(null);
    const [cvcError, setCvcError] = useState<string | null>(null);
    const dispatch = useDispatch();
    const { t } = useTranslation('stripePayment');
    const { initialMetadata } = useSelector(
      (state: any) => state?.questionnaireData,
    );
    const clientId = fromOnboarding ? initialMetadata?.user_id : userData?.id;
    const [billingCheckbox, setBillingCheckbox] = useState<boolean>(false);
    // if account/responsible address is an empty object, treat as no address data
    const hasAddressData =
      (accountAddress && Object.keys(accountAddress).length > 0) ||
      (responsiblePartyAccountAddress &&
        Object.keys(responsiblePartyAccountAddress).length > 0);
    const [addressState, setAddressState] =
      useState<AddressState>(addressInitialState);
    const [addressErrors, setAddressErrors] = useState<AddressErrors>(
      addressErrorInitialState,
    );
    const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const parentCardHolderName = userData?.metadata?.parent
      ? [
          userData.metadata.parent['first-name']?.trim(),
          userData.metadata.parent['last-name']?.trim(),
        ]
          .filter(Boolean)
          .join(' ')
      : null;

    useImperativeHandle(
      ref,
      () => ({
        submitCard: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }),
      [],
    );

    // Validate form and report validation state
    useEffect(() => {
      const isAddressValid =
        addressState?.address?.trim() &&
        addressState?.city?.trim() &&
        addressState?.state?.trim() &&
        addressState?.zip?.trim() &&
        addressState?.zip?.trim()?.length >= 5 &&
        addressState?.cardHolderName?.trim() &&
        !numberError &&
        !expiryError &&
        !cvcError &&
        (fromOnboarding ? isConsentChecked : true);

      setIsFormValid(!!isAddressValid);
      onFormValidationChange?.(!!isAddressValid);
    }, [
      addressState,
      numberError,
      expiryError,
      cvcError,
      isConsentChecked,
      fromOnboarding,
      onFormValidationChange,
    ]);

    useEffect(() => {
      onProcessingChange?.(processing);
    }, [processing, onProcessingChange]);

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();

      setProcessing(true);
      setError(null);
      setSuccess(false);

      const newErrors: AddressErrors = {
        address: !addressState?.address?.trim()
          ? t('stripeCard.errorMsg.addressError')
          : null,
        city: !addressState?.city?.trim()
          ? t('stripeCard.errorMsg.cityError')
          : null,
        state: !addressState?.state?.trim()
          ? t('stripeCard.errorMsg.stateError')
          : null,
        zip: !addressState?.zip?.trim()
          ? t('stripeCard.errorMsg.zipError')
          : addressState?.zip?.trim()?.length < 5
          ? t('stripeCard.errorMsg.zipDigitError')
          : null,
        cardHolderName: !addressState?.cardHolderName?.trim()
          ? t('stripeCard.errorMsg.cardHolderNameError')
          : null,
        cardNickName: null,
      };

      setAddressErrors(newErrors);

      if (Object.values(newErrors).some(error => error !== null)) {
        setProcessing(false);
        setError(t('stripeCard.errorMsg.missingAddress'));

        return;
      }

      if (!stripe || !elements) {
        Logger.error(t('stripeCard.errorMsg.stripeNotInitialized'));
        setError(t('stripeCard.errorMsg.tryLater'));

        if (fromOnboarding && onToken && !isDirectoryPlatform) {
          onToken(StripeOnTokenMethod?.ERROR, choice);
        }

        setProcessing(false);

        return;
      }

      try {
        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);

        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
          Logger.error(t('stripeCard.errorMsg.elementNotFound'));
          setError(t('stripeCard.errorMsg.elementNotFound'));

          if (fromOnboarding && onToken && !isDirectoryPlatform) {
            onToken(StripeOnTokenMethod?.ERROR, choice);
          }

          setProcessing(false);

          return;
        }

        // Check if consent is given
        if (fromOnboarding && !isConsentChecked) {
          setError(t('stripeCard.errorMsg.consentRequired'));
          setProcessing(false);

          return;
        }

        const { error: stripeError, setupIntent } =
          await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardNumberElement,
              billing_details: {
                name: addressState?.cardHolderName?.trim(),
                address: {
                  line1: addressState?.address?.trim(),
                  city: addressState?.city?.trim(),
                  postal_code: addressState?.zip?.trim(),
                  state: addressState?.state?.trim(),
                  country: 'US',
                },
              },
              metadata: {
                nickname: addressState?.cardNickName?.trim(),
              },
            },
          });

        if (stripeError) {
          setError(
            stripeError.message || t('stripeCard.errorMsg.validationFailed'),
          );

          if (fromOnboarding && onToken && !isDirectoryPlatform) {
            onToken(StripeOnTokenMethod?.ERROR, choice);
          }

          setProcessing(false);

          return;
        }

        if (!setupIntent) {
          setError(t('stripeCard.errorMsg.intentNotCreated'));

          if (fromOnboarding && onToken && !isDirectoryPlatform) {
            onToken(StripeOnTokenMethod?.ERROR, choice);
          }

          setProcessing(false);

          return;
        }

        if (setupIntent.status !== 'succeeded') {
          setError(
            `${t('stripeCard.errorMsg.intentFailed')} ${setupIntent.status}`,
          );

          if (fromOnboarding && onToken && !isDirectoryPlatform) {
            onToken(StripeOnTokenMethod?.ERROR, choice);
          }

          setProcessing(false);

          return;
        }

        const setupIntentId = setupIntent?.id;

        if (fromAddScreen) {
          const paymentResponse = await stripePaymentComplete(
            clientPaymentProfileId,
            setupIntentId,
            clientId,
          );

          if (paymentResponse) {
            const addMethods = await getStripePaymentMethods(clientId);

            if (addMethods?.payment_methods?.length > 0) {
              setSuccess(true);
              dispatch(
                setFlashNotification({
                  message: t('stripeCard.successMsg.addedSuccessfully'),
                }),
              );

              if (fromOnboarding && onToken && isConsentChecked) {
                onToken('consent_accepted: true', choice);
              }

              onCardUpdated('consent_accepted: true');
            } else {
              setError(t('stripeCard.errorMsg.failToFetch'));

              if (isConsentChecked) {
                if (fromOnboarding && onToken && !isDirectoryPlatform) {
                  onToken('consent_accepted: true', choice);
                }
              } else {
                if (fromOnboarding && onToken && !isDirectoryPlatform) {
                  onToken(StripeOnTokenMethod?.ERROR, choice);
                }
              }
            }
          } else {
            setError(t('stripeCard.errorMsg.paymentFailed'));

            if (fromOnboarding && onToken && !isDirectoryPlatform) {
              onToken(
                isConsentChecked
                  ? 'consent_accepted: true'
                  : StripeOnTokenMethod?.ERROR,
                choice,
              );
            }
          }
        } else {
          const paymentMethodId: any = setupIntent?.payment_method;

          if (!paymentMethodId) {
            setError(t('stripeCard.errorMsg.methodIdNotFound'));

            if (fromOnboarding && onToken && !isDirectoryPlatform) {
              onToken(StripeOnTokenMethod?.ERROR, choice);
            }

            setProcessing(false);

            return;
          }

          const updatedMethods = await putStripePaymentMethods(
            setupIntentId,
            paymentMethodId,
            clientId,
          );

          if (updatedMethods.payment_methods?.length > 0) {
            setSuccess(true);
            dispatch(
              setFlashNotification({
                message: t('stripeCard.successMsg.updatedSuccessfully'),
              }),
            );

            if (fromOnboarding && onToken && isConsentChecked) {
              onToken('consent_accepted: true', choice);
            }

            onCardUpdated('consent_accepted: true');
          } else {
            setError(t('stripeCard.errorMsg.failToFetch'));

            if (fromOnboarding && onToken && !isDirectoryPlatform) {
              onToken(StripeOnTokenMethod?.ERROR, choice);
            }
          }
        }
      } catch (error: any) {
        setError(error.message || t('stripeCard.errorMsg.unExpectedError'));

        if (fromOnboarding && onToken && !isDirectoryPlatform) {
          onToken(StripeOnTokenMethod?.ERROR, choice);
        }
      } finally {
        setProcessing(false);
      }
    };

    const handleAddressCheckbox = () => {
      setError(null);
      setBillingCheckbox(!billingCheckbox);
    };

    const handleInputChange =
      (field: keyof AddressState) => (e: ChangeEvent<HTMLInputElement>) => {
        setAddressState(prev => ({ ...prev, [field]: e.target.value }));
        setAddressErrors(prev => ({ ...prev, [field]: null }));
        setError(null);
      };

    const handleStateChange = (choices: any[]) => {
      const selectedState = choices.find(choice => choice.selected);

      if (selectedState) {
        setAddressState(prev => ({ ...prev, state: selectedState.value }));
        setAddressErrors(prev => ({ ...prev, state: null }));
      }

      setError(null);
    };

    useEffect(() => {
      // if there's no address data provided, ensure checkbox is turned off
      if (!hasAddressData) {
        setBillingCheckbox(false);
      }

      if (billingCheckbox) {
        const accountName = responsiblePartyAccountAddress
          ? responsiblePartyAccountAddress?.responsible_party_name
          : parentCardHolderName || accountAddress?.name || '';

        setAddressState({
          address: responsiblePartyAccountAddress
            ? responsiblePartyAccountAddress?.responsible_party_address
            : accountAddress?.address || '',
          city: responsiblePartyAccountAddress
            ? responsiblePartyAccountAddress?.responsible_party_city
            : accountAddress?.city || '',
          state: responsiblePartyAccountAddress
            ? responsiblePartyAccountAddress?.responsible_party_state
            : accountAddress?.state || '',
          zip: responsiblePartyAccountAddress
            ? responsiblePartyAccountAddress?.responsible_party_zipcode
            : accountAddress?.zipcode || '',
          cardHolderName: accountName,
          cardNickName: addressState.cardNickName || '',
        });
      } else {
        setAddressState({
          ...addressInitialState,
          cardNickName: addressState.cardNickName || '',
        });
      }

      setAddressErrors(addressErrorInitialState);
    }, [billingCheckbox, accountAddress, responsiblePartyAccountAddress]);

    return (
      <div className="stripe-card">
        <div className="stripe-card-back">
          {!hideBackButton && (
            <div
              className="stripe-card-back-icon"
              onClick={handleLeftArrowClick}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </div>
          )}
          <span>
            <img src={cardOnFileIcon} alt="card on file" />
            Add a credit card
          </span>
        </div>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="stripe-card-form"
        >
          <div className="stripe-card-input">
            <div className="stripe-card-input-header">
              {t('stripeCard.header.cardDetails')}
            </div>
            <div className="stripe-card-input-row">
              <div className="stripe-card-input-number">
                <label className="stripe-card-input-number-label">
                  {t('stripeCard.text.cardNumber')}
                </label>
                <CardNumberElement
                  options={{
                    showIcon: true,
                    placeholder: 'xxxx   xxxx    xxxx   1234',
                    disableLink: true,
                    disabled: processing,
                    style: {
                      base: {
                        fontSize: '14px',
                        color: '#354354',
                        '::placeholder': {
                          color: '#929aa6',
                        },
                        iconColor: '#667085',
                      },
                      invalid: {
                        color: '#dc1a1a',
                      },
                    },
                  }}
                  onChange={event => {
                    setNumberError(event.error?.message || null);
                    setError(null);
                  }}
                  className={
                    numberError
                      ? 'stripe-card-input-number-element-invalid'
                      : 'stripe-card-input-number-element'
                  }
                />
                {numberError && (
                  <div className="stripe-card-input-error">{numberError}</div>
                )}
              </div>
            </div>
            <div className="stripe-card-input-row">
              <div className="stripe-card-input-expiry">
                <label className="stripe-card-input-expiry-label">
                  {t('stripeCard.text.expiryDate')}
                </label>
                <CardExpiryElement
                  options={{
                    placeholder: 'MM/YY',
                    disabled: processing,
                    style: {
                      base: {
                        fontSize: '14px',
                        color: '#354354',
                        '::placeholder': {
                          color: '#929aa6',
                        },
                      },
                      invalid: {
                        color: '#dc1a1a',
                      },
                    },
                  }}
                  onChange={event => {
                    setExpiryError(event.error?.message || null);
                    setError(null);
                  }}
                  className={
                    expiryError
                      ? 'stripe-card-input-expiry-element-invalid'
                      : 'stripe-card-input-expiry-element'
                  }
                />
                {expiryError && (
                  <div className="stripe-card-input-error">{expiryError}</div>
                )}
              </div>
              <div className="stripe-card-input-cvc">
                <label className="stripe-card-input-cvc-label">
                  {t('stripeCard.text.cvv')}
                </label>
                <CardCvcElement
                  options={{
                    placeholder: '***',
                    disabled: processing,
                    style: {
                      base: {
                        fontSize: '14px',
                        color: '#354354',
                        '::placeholder': {
                          color: '#929aa6',
                        },
                      },
                      invalid: {
                        color: '#dc1a1a',
                      },
                    },
                  }}
                  onChange={event => {
                    setCvcError(event.error?.message || null);
                    setError(null);
                  }}
                  className={
                    cvcError
                      ? 'stripe-card-input-cvc-element-invalid'
                      : 'stripe-card-input-cvc-element'
                  }
                />
                {cvcError && (
                  <div className="stripe-card-input-error">{cvcError}</div>
                )}
              </div>
            </div>
            <div className="stripe-card-checkbox-row">
              <div className="stripe-card-checkbox-address">
                <label className="stripe-card-checkbox-address-label">
                  {t('stripeCard.text.cardNickName')}
                </label>
                <Input
                  type="text"
                  name="cardNickName"
                  value={addressState.cardNickName}
                  onChange={handleInputChange('cardNickName')}
                  placeholder={'Enter card nickname (optional)'}
                  required={false}
                  className={
                    addressErrors.cardNickName
                      ? 'stripe-card-checkbox-address-element-invalid'
                      : 'stripe-card-checkbox-address-element'
                  }
                  isCheckIconRequired={false}
                />
                {addressErrors.cardNickName && (
                  <div className="stripe-card-input-error">
                    {addressErrors.cardNickName}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="stripe-card-checkbox">
            <div className="stripe-card-checkbox-header">
              {t('stripeCard.header.billingAddress')}
            </div>
            {hasAddressData && (
              <MiniCheckBox
                text="Use previously entered address"
                isCheckFirst={true}
                isActive={billingCheckbox}
                onClick={handleAddressCheckbox}
                value=""
                className="stripe-card-checkbox-minibox"
              />
            )}
            <div className="stripe-card-checkbox-row">
              <div className="stripe-card-checkbox-address">
                <label className="stripe-card-checkbox-address-label">
                  {t('stripeCard.text.cardHolderName')}
                </label>
                <Input
                  type="text"
                  name="cardHolderName"
                  value={addressState.cardHolderName}
                  onChange={handleInputChange('cardHolderName')}
                  placeholder={'Enter name'}
                  required={true}
                  className={
                    addressErrors.cardHolderName
                      ? 'stripe-card-checkbox-address-element-invalid'
                      : 'stripe-card-checkbox-address-element'
                  }
                  isCheckIconRequired={false}
                  readOnly={billingCheckbox}
                />
                {addressErrors.cardHolderName && (
                  <div className="stripe-card-input-error">
                    {addressErrors.cardHolderName}
                  </div>
                )}
              </div>
            </div>
            <div className="stripe-card-checkbox-row">
              <div className="stripe-card-checkbox-address">
                <label className="stripe-card-checkbox-address-label">
                  {t('stripeCard.text.address')}
                </label>
                <Input
                  name="address"
                  value={addressState.address}
                  onChange={handleInputChange('address')}
                  type="text"
                  required={true}
                  placeholder={'Enter address'}
                  className={`mx-auto ${
                    addressErrors.address
                      ? 'stripe-card-checkbox-address-element-invalid'
                      : 'stripe-card-checkbox-address-element'
                  }`}
                  isCheckIconRequired={false}
                  readOnly={billingCheckbox}
                />
                {addressErrors.address && (
                  <div className="stripe-card-input-error">
                    {addressErrors.address}
                  </div>
                )}
              </div>
            </div>
            <div className="stripe-card-checkbox-row">
              <div className="stripe-card-checkbox-city">
                <label className="stripe-card-checkbox-city-label">
                  {t('stripeCard.text.city')}
                </label>
                <Input
                  name="city"
                  value={addressState.city}
                  onChange={handleInputChange('city')}
                  type="text"
                  required={true}
                  placeholder={'Enter city'}
                  className={`mx-auto ${
                    addressErrors.city
                      ? 'stripe-card-checkbox-city-element-invalid'
                      : 'stripe-card-checkbox-city-element'
                  }`}
                  isCheckIconRequired={false}
                  readOnly={billingCheckbox}
                />
                {addressErrors.city && (
                  <div className="stripe-card-input-error">
                    {addressErrors.city}
                  </div>
                )}
              </div>
            </div>
            <div className="stripe-card-checkbox-row">
              <div className="stripe-card-checkbox-state">
                <label className="stripe-card-checkbox-state-label">
                  {t('stripeCard.text.state')}
                </label>
                {billingCheckbox ? (
                  <Input
                    name="state"
                    value={addressState.state}
                    onChange={noop}
                    type="text"
                    placeholder={'State'}
                    className={`mx-auto stripe-card-checkbox-state-billing-element ${
                      addressErrors.state &&
                      'stripe-card-checkbox-state-element-invalid'
                    }`}
                    isCheckIconRequired={false}
                    readOnly={billingCheckbox}
                  />
                ) : (
                  <DropdownContainer
                    menu={usStatesChoices}
                    className={`mx-auto stripe-card-checkbox-state-element ${
                      addressErrors.state &&
                      'stripe-card-checkbox-state-element-invalid'
                    }`}
                    onChangeValue={handleStateChange}
                    title={addressState.state || 'Select state'}
                  />
                )}
                {addressErrors.state && (
                  <div className="stripe-card-input-error">
                    {addressErrors.state}
                  </div>
                )}
              </div>
              <div className="stripe-card-checkbox-zip">
                <label className="stripe-card-checkbox-zip-label">
                  {t('stripeCard.text.zip')}
                </label>
                <Input
                  name="zip"
                  value={addressState.zip}
                  onChange={(e: any) => {
                    // allow only digits and max 5 chars
                    const val = e.target.value.replace(/\D/g, '').slice(0, 5);

                    handleInputChange('zip')({ target: { value: val } } as any);
                  }}
                  type="text"
                  placeholder={'Zip code'}
                  className={`mx-auto ${
                    addressErrors.zip
                      ? 'stripe-card-checkbox-zip-element-invalid'
                      : 'stripe-card-checkbox-zip-element'
                  }`}
                  isCheckIconRequired={false}
                  readOnly={billingCheckbox}
                  maxLength={5}
                />
                {addressErrors.zip && (
                  <div className="stripe-card-input-error">
                    {addressErrors.zip}
                  </div>
                )}
              </div>
            </div>
          </div>

          {fromOnboarding ? (
            isDirectoryPlatform ? (
              <div className="stripe-card-consent">
                <div className="stripe-card-consent-content">
                  <div className="stripe-card-consent-agreement">
                    <div className="stripe-card-consent-checkbox-wrapper">
                      <MiniCheckBox
                        text={`I have read and agree to the full <a href='https://i2iconnect.com/payment-authorization-agreement/' target="_blank">Payment Authorization Agreement.</a>`}
                        isCheckFirst={true}
                        isActive={isConsentChecked}
                        onClick={() => setIsConsentChecked(prev => !prev)}
                        value=""
                        className="stripe-card-consent-checkbox-wrapper-minibox"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="stripe-card-consent">
                <div className="stripe-card-consent-header">
                  {t('stripeCard.authorization.consent')}
                </div>
                <div className="stripe-card-consent-content">
                  <p>{t('stripeCard.authorization.content')}</p>
                  <ul>
                    <li>{t('stripeCard.authorization.listItem1')}</li>
                    <li>{t('stripeCard.authorization.listItem2')}</li>
                  </ul>
                  <div className="stripe-card-consent-agreement">
                    <div className="stripe-card-consent-checkbox-wrapper">
                      <MiniCheckBox
                        text={`I have read and agree to the full <a href='https://i2iconnect.com/payment-authorization-agreement/' target="_blank">Payment Authorization Agreement.</a>`}
                        isCheckFirst={true}
                        isActive={isConsentChecked}
                        onClick={() => setIsConsentChecked(prev => !prev)}
                        value=""
                        className="stripe-card-consent-checkbox-wrapper-minibox"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : !userData?.metadata?.STRIPE_PAYMENTS_ENABLED ? (
            isDirectoryPlatform ? (
              <div className="stripe-card-consent">
                <div className="stripe-card-consent-content">
                  <div className="stripe-card-consent-agreement">
                    <div className="stripe-card-consent-checkbox-wrapper">
                      <MiniCheckBox
                        text={`I have read and agree to the full <a href='https://i2iconnect.com/payment-authorization-agreement/' target="_blank">Payment Authorization Agreement.</a>`}
                        isCheckFirst={true}
                        isActive={isConsentChecked}
                        onClick={() => setIsConsentChecked(prev => !prev)}
                        value=""
                        className="stripe-card-consent-checkbox-wrapper-minibox"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="stripe-card-consent">
                <div className="stripe-card-consent-header">
                  {t('stripeCard.authorization.consent')}
                </div>
                <div className="stripe-card-consent-content content-flex">
                  <div>
                    {t('stripeCard.authorization.authorizationEmail')}{' '}
                    <span className="text-bold">{userData?.email}</span>
                  </div>
                  <div>
                    {t('stripeCard.authorization.authorizationEmailNote')}
                  </div>
                </div>
              </div>
            )
          ) : (
            <></>
          )}
          {error && (
            <Alert type="error" className="stripe-card-alert" message={error} />
          )}
          {success && (
            <Alert
              type="success"
              className="stripe-card-alert"
              message={t('stripeCard.successMsg.savedSuccessfully')}
            />
          )}

          <StripePoweredBy
            buttonLabel={buttonLabel}
            processing={processing}
            disabled={
              fromOnboarding
                ? !isConsentChecked
                : isDirectoryPlatform
                ? !userData?.metadata?.STRIPE_PAYMENTS_ENABLED
                  ? !isConsentChecked
                  : false
                : false
            }
            isFromOnboarding={fromOnboarding}
          />
        </form>
      </div>
    );
  },
);

export default StripeCard;
